import { MTableToolbar, MaterialTableProps, Query } from "@material-table/core";
import { Alert, Box, Button } from "@mui/material";
import { useEffect, useState, useRef, useMemo } from "react";
import { Container, Breadcrumb } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";

import { AUTH_ROLES, PERMISSION_AREAS } from "api/common/types/auth.types";
import { useLazyEcrsQuery } from "api/memberPortal/ecr/ecr.api";
import { Ecr } from "api/memberPortal/ecr/ecr.types";
import { useEcrTemplateMutation } from "api/memberPortal/ecr/excel/ecrExcel.api";
import ResponsiveMaterialTable from "components/UI/MaterialTable/ResponsiveMaterialTable";
import UnauthorizedView from "components/UI/UnauthorizedView";
import { useAuth } from "hooks/useAuth";
import { checkPermissions } from "utils/permissions";

import EcrCommentForm from "../EcrCommentForm/EcrCommentForm";
import buildEcrCommentsTableColumns from "../EcrCommentsTable/EcrCommentsTable.columns";

type AlertType = {
  message: string;
  type: "error" | "success";
};

const tableOptions = {
  headerStyle: {
    fontWeight: "bold",
    borderBottom: "none",
    paddingBottom: "0",
  },
  filtering: true,
  columnsButton: true,
  showSelectAllCheckbox: false,
  pageSize: 20,
};

const EcrOverview = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { permissions } = useAuth();
  const [showCommentForm, setShowCommentForm] = useState(false);

  const tableRef = useRef<MaterialTableProps<Ecr>>();

  const [alert, setAlert] = useState<AlertType>();

  const [triggerEcrsQuery] = useLazyEcrsQuery();

  const columns = buildEcrCommentsTableColumns({
    setShowCommentForm, // TODO: honestly can probably eventually get rid of this too which would
    //                           let this be a simple array instead of needing a function
  });

  useEffect(() => {
    if (
      location.pathname === "/community/ecr-comments" ||
      location.pathname === "/community/ecr-comments/"
    ) {
      document.title = "NIAP Community - ECR Comments Table";
    }
  }, [location.pathname]);

  const resetAlerts = () => setAlert(undefined);

  const handleDownloadTemplate = async () => {
    triggerTemplateDownload()
      .unwrap()
      .then(() => {
        setAlert({
          message: "Successfully downloaded ECR Import Template!",
          type: "success",
        });
      })
      .catch((error) => {
        console.error("Error downloading Excel file:", error);
        setAlert({
          message: "Error downloading ECR Import Template.",
          type: "error",
        });
      });
  };

  const [triggerTemplateDownload] = useEcrTemplateMutation();

  const actions = useMemo(() => {
    const refreshAction = {
      icon: "refresh",
      tooltip: "Refresh Data",
      isFreeAction: true,
      onClick: () =>
        tableRef.current &&
        // @ts-ignore
        tableRef.current.dataManager.columns.forEach((item) => {
          // @ts-ignore
          tableRef.current.onFilterChange(item.tableData.id, "");
        }),
    };

    const closeAction = {
      icon: "close",
      tooltip: "Clear Filters",
      isFreeAction: true,
      onClick: () => {
        let self = tableRef.current;
        // @ts-ignore - ignore until DataGrid
        let query = self.state.query;
        // @ts-ignore - ignore until DataGrid
        self.setState({
          // @ts-ignore - ignore until DataGrid
          ...self.dataManager.getRenderState(),
          query: {
            ...query,
            filters: [],
          },
        });
        // @ts-ignore - ignore until DataGrid
        for (let item in tableRef.current.state.query.filters) {
          // @ts-ignore - ignore until DataGrid
          let tableColumn = tableRef.current.state.query.filters[item].column;
          // @ts-ignore - ignore until DataGrid
          tableRef.current.dataManager.changeFilterValue(
            tableColumn.tableData.id,
            null,
          );
          // @ts-ignore - ignore until DataGrid
          tableRef.current.onFilterChange(tableColumn.tableData.id, "");
        }
      },
    };
    return [refreshAction, closeAction];
  }, []);

  const canViewEcrs = checkPermissions({
    permissions,
    area: PERMISSION_AREAS.ECR_COMMENTS,
  });

  if (!canViewEcrs) return <UnauthorizedView />;

  const isLab = permissions.role_type === AUTH_ROLES.LAB;

  return (
    <Container fluid className="my-3">
      <Breadcrumb>
        <Breadcrumb.Item onClick={() => navigate(`/`)}>Home</Breadcrumb.Item>
        <Breadcrumb.Item onClick={() => navigate(`/community/`)}>
          Community
        </Breadcrumb.Item>
        <Breadcrumb.Item active>ECR Comments</Breadcrumb.Item>
      </Breadcrumb>

      {alert && (
        <Alert sx={{ marginY: 2 }} severity={alert.type} onClose={resetAlerts}>
          {alert.message}
        </Alert>
      )}

      {!isLab && (
        <Box display="flex" justifyContent="flex-end" pb={2} gap={2}>
          <Button
            variant="contained"
            color="primary"
            sx={{ borderRadius: 0, textTransform: "none" }}
            onClick={() => {
              setShowCommentForm(true);
            }}
          >
            + Add ECR Comment
          </Button>

          <Button
            variant="contained"
            color="success"
            sx={{ borderRadius: 0, textTransform: "none" }}
            onClick={handleDownloadTemplate}
          >
            Download ECR Template
          </Button>
        </Box>
      )}

      {showCommentForm && (
        <EcrCommentForm
          show={showCommentForm}
          onClose={() => setShowCommentForm(false)}
          onError={() =>
            setAlert({
              message: "Error submitting ECR comment. Please try again later.",
              type: "error",
            })
          }
          onSuccess={() => {
            setAlert({
              message: "Successfully submitted ECR comment!",
              type: "success",
            });

            tableRef.current?.onQueryChange?.();
          }}
        />
      )}

      <ResponsiveMaterialTable
        title="ECR Comments"
        columns={columns}
        tableRef={tableRef}
        actions={actions}
        components={{
          // @ts-ignore : This isnt fully typed and tbh idk that I ever will with trying to transition to DataGrid
          Toolbar: (props) => <MTableToolbar {...props} />,
        }}
        data={(query: Query<Ecr>) =>
          new Promise((resolve, reject) => {
            // Extract the necessary information from the query object
            const { page, pageSize, search, filters, orderBy, orderDirection } =
              query;

            let responsivePageSize = pageSize;

            let newFilters = filters.map((filter) => {
              let value = "";
              if (Array.isArray(filter.value)) {
                // @ts-ignore : Honestly just gonna ignore all of these
                value = filter.value;
              } else if (
                // @ts-ignore : Honestly just gonna ignore all of these
                ["entrydate", "moddate"]?.includes(filter?.column?.field)
              ) {
                const dateObj = new Date(filter?.value);
                const isoStr = dateObj.toISOString();
                const formattedStr = isoStr.split("T")[0];
                value = formattedStr;
              } else {
                value = filter.value.replace(/['"]+/g, "");
              }
              // @ts-ignore : Honestly just gonna ignore all of these
              return `"${filter.column.field.replace(/\./g, "__")}":"${value}"`;
            });

            const params = {
              offset: page * responsivePageSize,
              limit: responsivePageSize,
              filters: newFilters,
              orderBy: orderBy?.field || "ecrid",
              orderDirection: orderDirection,
              get_unreleased: "true",
              ...(search && { search }),
            };

            triggerEcrsQuery(params)
              .then((response) => {
                if (!response.data) throw new Error("Empty Response");

                resolve({
                  data: response.data.results,
                  page: page,
                  totalCount: response.data.count,
                });
              })
              .catch((error) => {
                reject(error);
              });
          })
        }
        options={tableOptions}
      />
    </Container>
  );
};

export default EcrOverview;
